import {
  AlignLeftOutlined,
  BgColorsOutlined,
  ColumnWidthOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  LockOutlined,
  NumberOutlined,
  TagOutlined,
  UnlockOutlined
} from '@ant-design/icons';
import { Button, Input, Popconfirm, Popover, Skeleton, Switch, Tooltip, Typography } from 'antd';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { CustomPicker } from 'react-color';
import { Hue } from 'react-color/lib/components/common';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Node } from './MelodyGraph';
import { Pattern, PatternLabelGenerator } from './models/Pattern';

export const melodicPatternConfigurationID = 'melodic-pattern-configuration';

// A button, so the colour can also be changed with the keyboard (audit ISSUE-022).
const ColoredPatch = styled.button<{ $color: string }>`
  background-color: ${(styles) => styles.$color};
  border: 0;
  border-radius: 0.2rem;
  cursor: pointer;
  display: block;
  height: 15px;
  padding: 0;
  width: 100px;
`;

const HueWrapper = styled.div`
  width: -fill-available;

  div > .hue-horizontal > div > div {
    height: 22px !important;
  }
`;

const SmallInput = styled(Input)`
  width: 100px;
`;

// antd 6 sizes the skeleton with more specific selectors than antd 4 and only gives the class name to the wrapper, not
// to the inner element any more: the repeated & outweighs antd's selectors, the inner element is sized explicitly.
const SmallSkeletonInput = styled(Skeleton.Input)<{ $stretch?: boolean }>`
  &&& {
    height: 22px !important;
    width: ${(styles) => (styles.$stretch ? '-webkit-fill-available' : '100px')};
  }

  &&& .ant-skeleton-input {
    height: 22px;
    min-width: unset;
    width: ${(styles) => (styles.$stretch ? '-webkit-fill-available' : '100px')};
  }
`;

const { Text, Title } = Typography;

const ColorPicker = CustomPicker((props) => {
  const [intermediateColor, setIntermediateColor] = useState<string>(props.hex);

  return (
    <div className="grid grid-cols-2 gap-x-1">
      <HueWrapper className="relative h-[24px] p-1">
        <Hue {...props} onChange={props.onChange} />
      </HueWrapper>
      <Input
        maxLength={7}
        onBlur={() => setIntermediateColor(props.hex)}
        onChange={(event) => {
          const color = event.target.value;

          setIntermediateColor(color);
          if (/^#[0-9A-F]{6}$/i.test(color)) props.onChange(color);
        }}
        prefix={<BgColorsOutlined />}
        size="small"
        value={intermediateColor}
      />
    </div>
  );
});

const PatternConfiguration = ({
  configuringID,
  nodes,
  patterns,
  setConfiguringID,
  setNodes,
  setPatterns,
  setSuggestingID
}: {
  configuringID: string;
  nodes: Node[];
  patterns: Pattern[];
  setConfiguringID: Dispatch<SetStateAction<string>>;
  setNodes: Dispatch<SetStateAction<Node[]>>;
  setPatterns: Dispatch<SetStateAction<Pattern[]>>;
  setSuggestingID: Dispatch<SetStateAction<string>>;
}) => {
  const pattern = patterns.find((pattern) => pattern.id === configuringID);

  const [color, setColor] = useState(pattern?.color);
  const [labelDraft, setLabelDraft] = useState<string>();
  // The button's tooltip would cover the confirmation's buttons.
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const isLabelEmpty = labelDraft !== undefined && labelDraft.trim() === '';
  const { t: translate } = useTranslation();

  useEffect(() => {
    setColor(pattern?.color);
    setLabelDraft(undefined);
  }, [configuringID]);

  return (
    <div className="flex items-center justify-between" id={melodicPatternConfigurationID}>
      <Title
        className="!m-0 text-center rotate-180 whitespace-pre-wrap !leading-none"
        level={5}
        style={{ writingMode: 'vertical-rl' }}
      >
        {translate('melodicPattern.configuration.title')}
      </Title>
      <div
        className="pb-4 pr-2 pt-4 grid gap-x-4 gap-y-2"
        style={{
          gridTemplateColumns: 'repeat(4, min-content(1px))'
        }}
      >
        <div>
          <span className="mb-1 flex items-center text-slate-600 text-xs">
            <TagOutlined className="mr-1" />
            {translate('melodicPattern.configuration.label')}
          </span>
          {configuringID === undefined ? (
            <SmallSkeletonInput size="small" />
          ) : (
            <SmallInput
              aria-invalid={isLabelEmpty}
              aria-label={translate('melodicPattern.configuration.label')}
              maxLength={50}
              // An empty label is shown while typing but never stored (audit ISSUE-016: "undefined..." in the
              // timeline); leaving the field restores the last label.
              onBlur={() => setLabelDraft(undefined)}
              onChange={(event) => {
                const label = event.target.value;
                setLabelDraft(label);
                if (label.trim() === '') return;

                setPatterns((oldPatterns) => {
                  const patternToChange = oldPatterns.find(
                    (pattern) => pattern.id === configuringID
                  );

                  if (patternToChange !== undefined) {
                    patternToChange.label = label;

                    return [...oldPatterns];
                  } else {
                    return oldPatterns;
                  }
                });
              }}
              size="small"
              status={isLabelEmpty ? 'error' : undefined}
              value={labelDraft ?? pattern?.label}
              variant="borderless"
            />
          )}
        </div>
        <div>
          <span className="mb-1 flex items-center text-slate-600 text-xs">
            <BgColorsOutlined className="mr-1" />
            {translate('melodicPattern.configuration.color')}
            <Tooltip
              classNames={{ root: '!text-xs' }}
              title={translate('melodicPattern.configuration.disclaimer')}
            >
              <InfoCircleOutlined className="ml-1" style={{ fontSize: 'xx-small' }} />
            </Tooltip>
          </span>
          {configuringID === undefined ? (
            <SmallSkeletonInput size="small" />
          ) : (
            <Popover
              content={
                <ColorPicker
                  color={color || pattern?.color}
                  onChange={(color) => setColor(color.hex)}
                  onChangeComplete={(color) =>
                    setPatterns((oldPatterns) => {
                      const patternToChange = oldPatterns.find(
                        (pattern) => pattern.id === configuringID
                      );

                      if (patternToChange !== undefined) {
                        patternToChange.color = color.hex;

                        setColor(color.hex);

                        return [...oldPatterns];
                      } else {
                        return oldPatterns;
                      }
                    })
                  }
                />
              }
              id="color-picker"
              styles={{ container: { padding: '0.5rem', width: 250 }, content: { padding: 0 } }}
              trigger="click"
            >
              <ColoredPatch
                $color={pattern?.color}
                aria-label={translate('melodicPattern.configuration.color')}
                className="ml-1 mt-2"
                type="button"
              />
            </Popover>
          )}
        </div>
        <div>
          <span className="mb-1 flex items-center text-slate-600 text-xs">
            <ColumnWidthOutlined className="mr-1" />
            {translate('melodicPattern.configuration.length')}
          </span>
          {configuringID === undefined ? (
            <SmallSkeletonInput size="small" />
          ) : (
            <Text className="ml-1.5">{pattern?.length}</Text>
          )}
        </div>
        <div>
          <span className="mb-1 flex items-center text-slate-600 text-xs whitespace-nowrap">
            <NumberOutlined className="mr-1" />
            {translate('melodicPattern.configuration.occurrences')}
          </span>
          {configuringID === undefined ? (
            <SmallSkeletonInput $stretch size="small" />
          ) : (
            <Text className="ml-1.5">
              {pattern?.occurrences.length} ({pattern?.uniqueOccurrencesCount})
            </Text>
          )}
        </div>
        <div style={{ gridColumn: 'span 4' }}>
          <span className="mb-1 flex items-center text-slate-600 text-xs">
            <AlignLeftOutlined className="mr-1" />
            {translate('melodicPattern.configuration.description')}
          </span>
          {configuringID === undefined ? (
            <SmallSkeletonInput $stretch size="small" />
          ) : (
            <Input
              maxLength={280}
              onChange={(event) =>
                setPatterns((oldPatterns) => {
                  const patternToChange = oldPatterns.find(
                    (pattern) => pattern.id === configuringID
                  );

                  if (patternToChange !== undefined) {
                    patternToChange.description = event.target.value;

                    return [...oldPatterns];
                  } else {
                    return oldPatterns;
                  }
                })
              }
              placeholder={translate('melodicPattern.configuration.descriptionPlaceholder')}
              size="small"
              value={pattern?.description}
              variant="borderless"
            />
          )}
        </div>
      </div>
      <div className="h-full flex flex-col items-center justify-center">
        <Switch
          aria-label={translate('melodicPattern.configuration.lock')}
          checked={pattern?.isLocked}
          checkedChildren={<LockOutlined />}
          disabled={configuringID === undefined}
          onChange={(checked) => {
            const nodeIndexToUnlock = nodes.findIndex((node) => node.id === configuringID);

            // With individual entries shown, a group has no node of its own to pin.
            if (nodeIndexToUnlock === -1) {
              // Nothing to pin in the layout; the pattern keeps its lock flag below.
            } else if (checked) {
              (nodes[nodeIndexToUnlock] as Node).fx = nodes[nodeIndexToUnlock].x;
              (nodes[nodeIndexToUnlock] as Node).fy = nodes[nodeIndexToUnlock].y;
            } else {
              delete (nodes[nodeIndexToUnlock] as Node).fx;
              delete (nodes[nodeIndexToUnlock] as Node).fy;
            }

            setNodes([...nodes]);
            setPatterns((oldPatterns) => {
              const patternToChange = oldPatterns.find(
                (oldPattern) => oldPattern.id === configuringID
              );

              patternToChange.isLocked = checked;

              return [...oldPatterns];
            });
          }}
          size="small"
          unCheckedChildren={<UnlockOutlined />}
        />
        {/* Deleting asks first; suggested patterns explain why they cannot be deleted (audit ISSUE-019). */}
        <Popconfirm
          cancelText={translate('toolbar.cancel')}
          disabled={configuringID === undefined || !pattern?.isUserDefined}
          okButtonProps={{ danger: true }}
          okText={translate('toolbar.delete')}
          onOpenChange={setIsConfirmingDelete}
          onConfirm={() => {
            setPatterns((oldPatterns) => {
              const patternToDelete = oldPatterns.find(
                (oldPattern) => oldPattern.id === configuringID
              );
              const patternToDeleteIndex = oldPatterns.findIndex(
                (oldPattern) => oldPattern.id === configuringID
              );

              if (patternToDeleteIndex !== -1) {
                if (patternToDelete && patternToDelete.isUserDefined) {
                  patternToDelete.connectedToByOperator.forEach((target) => {
                    const targetPatternIndex = oldPatterns.findIndex(
                      (oldPattern) => oldPattern.id === target.id
                    );

                    if (targetPatternIndex !== -1) oldPatterns.splice(targetPatternIndex, 1);
                  });
                }

                oldPatterns.splice(patternToDeleteIndex, 1);
                PatternLabelGenerator.add(patternToDelete.label);
              }

              return [...oldPatterns];
            });
            setConfiguringID(undefined);
            setSuggestingID(undefined);
          }}
          title={translate('melodicPattern.configuration.deleteConfirm', { label: pattern?.label })}
        >
          <Tooltip
            open={isConfirmingDelete ? false : undefined}
            title={translate(
              pattern && !pattern.isUserDefined
                ? 'melodicPattern.configuration.deleteSuggested'
                : 'melodicPattern.configuration.delete'
            )}
          >
            {/* A disabled button gets no pointer events; the span keeps its tooltip. */}
            <span className="mt-2">
              <Button
                aria-label={translate('melodicPattern.configuration.delete')}
                danger
                disabled={configuringID === undefined || !pattern?.isUserDefined}
                icon={<DeleteOutlined />}
                size="small"
                type="text"
              />
            </span>
          </Tooltip>
        </Popconfirm>
      </div>
    </div>
  );
};

export default PatternConfiguration;
